import { RESERVATIONS_BASE_KEY } from '@iheartjane/data-access';
import type { CustomerReservationDetails } from '@iheartjane/data-access';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getJwt } from '@jane/shared/auth';

import { janeApi } from '../api/janeApi';
import type {
  ReservationOwnershipsProps,
  ReservationRequestProps,
  ReviewReservationProps,
} from './reservations.types';
import { reservationsUrls } from './reservations.util';

interface NotifyCurbsideArrivalProps {
  details: {
    color?: string;
    make?: string;
    model?: string;
  };
  reservationId?: string | number;
  uuid: string;
}

interface MutationProps {
  onError?: () => void;
  onSuccess?: () => void;
}

const notifyCurbsidePickupArrival = async ({
  uuid,
  details,
}: NotifyCurbsideArrivalProps) => {
  return await janeApi.post(
    reservationsUrls.notifyCurbsideArrival({ id: uuid }),
    { customer_details: details },
    getJwt()
  );
};

const reviewReservation = async ({ id, review }: ReviewReservationProps) => {
  return await janeApi.post(
    reservationsUrls.reviewReservation({ id }),
    review,
    getJwt()
  );
};

const cancelReservation = async ({ id }: ReservationRequestProps) => {
  return await janeApi.delete(
    reservationsUrls.cancelReservation({ id }),
    getJwt()
  );
};

const cancelGuestReservation = async ({ id }: ReservationRequestProps) => {
  return await janeApi.delete(
    reservationsUrls.cancelGuestReservation(),
    getJwt(),
    { body: JSON.stringify({ uuid: id }) }
  );
};

const reservationOwnerships = async ({ id }: ReservationOwnershipsProps) => {
  const res = await janeApi.post<{ reservation: CustomerReservationDetails }>(
    reservationsUrls.reservationOwnerships({ id }),
    null,
    getJwt()
  );
  return res.reservation;
};

export const useNotifyCurbsidePickupArrival = ({
  onError,
  onSuccess,
}: MutationProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notifyCurbsidePickupArrival,
    onSuccess: () => {
      onSuccess && onSuccess();

      queryClient.invalidateQueries({ queryKey: [RESERVATIONS_BASE_KEY] });
    },
    onError,
  });
};

export const useCreateReservationReview = ({
  onError,
  onSuccess,
}: MutationProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reviewReservation,
    onSuccess: () => {
      onSuccess && onSuccess();

      queryClient.invalidateQueries({ queryKey: [RESERVATIONS_BASE_KEY] });
    },
    onError,
  });
};

export const useCancelReservation = ({ onError, onSuccess }: MutationProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelReservation,
    onSuccess: () => {
      onSuccess && onSuccess();

      queryClient.invalidateQueries({ queryKey: [RESERVATIONS_BASE_KEY] });
    },
    onError,
  });
};

export const useCancelGuestReservation = ({
  onError,
  onSuccess,
}: MutationProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelGuestReservation,
    onSuccess: () => {
      onSuccess && onSuccess();

      queryClient.invalidateQueries({ queryKey: [RESERVATIONS_BASE_KEY] });
    },
    onError,
  });
};

export const useCreateReservationOwnerships = () => {
  return useMutation({
    mutationFn: reservationOwnerships,
  });
};
